import AttachmentField from './AttachmentField';
import { submitRequest } from '../lib/submitRequest';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ClipboardCheck, Layers3, ShieldCheck } from 'lucide-react';
import { useToast } from '../hooks/use-toast';


const QUOTE_GROUPS = {
  "web": [
    [
      "landing-page",
      "Landing Page",
      "A partir de 149€",
      "Une page unique et responsive pour présenter rapidement une activité et faciliter la prise de contact."
    ],
    [
      "site-vitrine",
      "Site vitrine",
      "Sur devis",
      "Un site professionnel de plusieurs pages pour présenter une structure, ses services, son équipe et ses références."
    ],
    [
      "e-commerce",
      "E-commerce",
      "Sur devis",
      "Une boutique en ligne avec catalogue, fiches produits et parcours de commande adapté au besoin."
    ],
    [
      "refonte-site",
      "Refonte de site",
      "Sur devis",
      "La modernisation d’un site existant pour améliorer son image, sa structure et son expérience mobile."
    ]
  ],
  "saas": [
    [
      "saas",
      "SaaS / logiciel métier",
      "",
      "Un outil évolutif conçu autour des processus et des besoins propres à votre activité."
    ],
    [
      "application-web",
      "Application web",
      "",
      "Une interface professionnelle accessible depuis vos différents appareils pour centraliser les usages."
    ],
    [
      "crm",
      "CRM / outil interne",
      "",
      "Un espace commun pour réunir clients, opérations, équipes et indicateurs de pilotage."
    ],
    [
      "automatisation",
      "Automatisation",
      "",
      "Des flux simples et fiables pour réduire les tâches répétitives et gagner en efficacité."
    ]
  ],
  "hosting": [
    [
      "hebergement-standard",
      "Standard",
      "120 € / an",
      "Une base d’hébergement fiable pour démarrer, avec domaine, stockage SSD et certificat SSL."
    ],
    [
      "hebergement-premium",
      "Premium",
      "250 € / an",
      "Une capacité renforcée pour les sites en croissance qui accueillent davantage de trafic."
    ],
    [
      "hebergement-platinium",
      "Platinium",
      "Sur devis",
      "Une solution complète destinée aux structures ayant des besoins élevés en trafic et en stockage."
    ]
  ],
  "maintenance": [
    [
      "maintenance-essentiel",
      "Essentiel",
      "",
      "La protection indispensable avec antivirus, pare-feu, blocage des attaques et alertes de sécurité."
    ],
    [
      "maintenance-pro",
      "Pro",
      "",
      "Une sécurité avancée avec surveillance quotidienne, double authentification et support prioritaire."
    ],
    [
      "maintenance-serenite",
      "Sérénité",
      "",
      "Une maintenance complète avec surveillance active, audit annuel et intervention contre les logiciels malveillants."
    ]
  ],
  "complementary": [
    [
      "identite-visuelle",
      "Identité visuelle",
      "",
      "Un univers cohérent regroupant logo, couleurs, typographies et déclinaisons de marque."
    ],
    [
      "strategie-digitale",
      "Stratégie digitale",
      "",
      "Un plan d’action qui précise les canaux, les messages et les priorités de communication."
    ],
    [
      "reseaux-sociaux",
      "Réseaux sociaux",
      "",
      "Un accompagnement éditorial et visuel adapté aux plateformes et aux objectifs de la structure."
    ],
    [
      "formations",
      "Formations",
      "",
      "Des parcours pour développer les compétences professionnelles, managériales, commerciales et digitales."
    ]
  ]
};
const TRAINING_CHOICES = ['Vente & Prospection','Leadership & management','Prise de parole & impact','Relation client','Entrepreneuriat','Insertion professionnelle',"Finance & gestion d’argent",'Digital & outils professionnels'];

export default function QuoteBuilder({ mode = 'digital' }) {
  const location = useLocation();
  const { toast } = useToast();
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  const [quoteSending, setQuoteSending] = useState(false);
  const [quoteStep, setQuoteStep] = useState(1);
  const quoteMode = mode;
  const [quote, setQuote] = useState({ name:'', email:'', phone:'', company:'', service:mode === 'training' ? ['formations'] : [], trainingChoices:[], trainingType:'', trainingParticipants:'', trainingLocation:'', deadline:'', details:'', consent:false });

  useEffect(() => {
    if (!quoteSubmitted) return undefined;
    const timer = setTimeout(() => setQuoteSubmitted(false), 10000);
    return () => clearTimeout(timer);
  }, [quoteSubmitted]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requested = params.get('service');
    const requestedTraining = params.get('formation');
    const clearTrainingSelection = mode === 'training' && location.state?.clearTrainingSelection;
    if (clearTrainingSelection || (mode === 'training' && requestedTraining)) setQuoteStep(1);
    const matchedTraining = requestedTraining && TRAINING_CHOICES.find((choice) => choice.toLocaleLowerCase('fr') === requestedTraining.toLocaleLowerCase('fr'));
    if (mode === 'training' || (requested && requested !== 'formations')) {
      const trainingRequest = mode === 'training';

      setQuote((current) => ({
        ...current,
        service: trainingRequest ? ['formations'] : [requested === 'maintenance-securite' ? 'maintenance-pro' : requested],
        trainingChoices: clearTrainingSelection ? [] : trainingRequest && requestedTraining ? [matchedTraining || requestedTraining] : current.trainingChoices,
      }));
    }
    if (location.hash === '#formulaire-devis' || location.hash === '#choix-formations') {
      setTimeout(() => document.getElementById('formulaire-devis')?.scrollIntoView({ behavior:'smooth', block:'start' }), 120);
    }
  }, [location.search, location.hash, location.key, location.state, mode]);


  const toggleService = (value) => setQuote((current) => {
    const removing = current.service.includes(value);
    return {
      ...current,
      service: removing ? current.service.filter(x => x !== value) : [...current.service,value],
      ...(removing && value === 'formations' ? { trainingChoices:[], trainingType:'', trainingParticipants:'', trainingLocation:'' } : {}),
    };
  });
  const chooseSingle = (value, group) => setQuote((current) => ({ ...current, service: [...current.service.filter(x => !group.includes(x)), ...(current.service.includes(value) ? [] : [value])] }));
  const webSelected = QUOTE_GROUPS.web.map(x=>x[0]).filter(x=>quote.service.includes(x));
  const saasSelected = QUOTE_GROUPS.saas.map(x=>x[0]).filter(x=>quote.service.includes(x));
  const hostingValues = QUOTE_GROUPS.hosting.map(x=>x[0]);
  const maintenanceValues = QUOTE_GROUPS.maintenance.map(x=>x[0]);
  const digitalComplementary = QUOTE_GROUPS.complementary.filter(([value])=>value!=='formations');
  const standardDisabled = quote.service.includes('e-commerce') || saasSelected.length > 0 || webSelected.length > 1;
  useEffect(() => { if (standardDisabled && quote.service.includes('hebergement-standard')) setQuote((current) => ({ ...current, service: current.service.filter(x=>x !== 'hebergement-standard') })); }, [standardDisabled, quote.service]);
  const hostingMessage = quote.service.includes('e-commerce')
    ? 'Site e-commerce sélectionné : l’hébergement doit être au minimum Premium. Le pack Standard est désactivé.'
    : (saasSelected.length > 0 && webSelected.length > 0)
      ? 'Site web et outil métier associés : choisissez Premium ou Platinium pour supporter l’ensemble de la solution.'
      : saasSelected.length > 1
        ? 'Plusieurs outils métiers sélectionnés : Premium devient le niveau minimum recommandé et Standard est indisponible.'
        : saasSelected.length === 1
          ? 'Solution SaaS sélectionnée : privilégiez Premium ou Platinium pour la disponibilité, les données et les évolutions.'
          : webSelected.length > 1
            ? 'Plusieurs projets web sélectionnés : une infrastructure Premium minimum est nécessaire pour les regrouper.'
      : quote.service.includes('landing-page')
        ? 'Landing Page sélectionnée : un hébergement Standard suffit largement.'
        : (quote.service.includes('site-vitrine') || quote.service.includes('refonte-site'))
          ? 'Solution digitale sélectionnée : choisissez l’hébergement le plus adapté au niveau du projet.'
          : 'Vous pouvez choisir une formule d’hébergement seule ou l’associer à votre solution digitale.';
  const goToQuoteDetails = () => {
    if (!quote.service.length) { toast({ title:'Choisissez au moins un service', description:'Sélectionnez une prestation avant de passer à l’étape suivante.' }); return; }
    if (quote.service.includes('formations') && !quote.trainingChoices.length) { toast({ title:'Choisissez une formation', description:'Sélectionnez au moins un domaine de formation.' }); return; }
    setQuoteStep(2);
  };
  const submitQuote = async (e) => {
    e.preventDefault();
    const formElement = e.currentTarget;
    if (!quote.service.length) {
      toast({ title:'Choisissez au moins un service', description:'Cette information permet de diriger votre demande vers la bonne expertise.' });
      return;
    }
    setQuoteSending(true);
    setQuoteSubmitted(false);
    try {
      const serviceLabels = Object.values(QUOTE_GROUPS).flat().filter(([value]) => quote.service.includes(value)).map(([,label,price]) => price ? `${label} (${price})` : label);
      await submitRequest(quoteMode === 'training' ? 'training' : 'quote', {
        subject: quoteMode === 'training' ? `Nouvelle demande de devis formation MCE — ${quote.name}` : `Nouvelle demande de devis MCE — ${quote.name}`,
        type_de_demande: quoteMode === 'training' ? 'Demande de devis formation' : 'Demande de devis',
        nom: quote.name,
        email: quote.email,
        telephone_whatsapp: quote.phone,
        entreprise: quote.company || 'Non renseignée',
        services_demandes: serviceLabels.join(', '),
        formations_choisies: quote.trainingChoices.length ? quote.trainingChoices.join(', ') : 'Non concerné',
        type_de_formation: quote.service.includes('formations') ? quote.trainingType : 'Non concerné',
        nombre_de_participants: quote.service.includes('formations') ? quote.trainingParticipants : 'Non concerné',
        lieu_de_la_seance: quote.service.includes('formations') && quote.trainingType !== 'En ligne' ? quote.trainingLocation : 'Non concerné',
        delai_souhaite: quote.deadline || 'À définir',
        details_du_projet: quote.details || 'Aucun détail complémentaire',
        consentement: quote.consent ? 'Accepté' : 'Non accepté',
      }, formElement);
      setQuoteSubmitted(true);
      toast({ title:'Demande de devis envoyée', description:'MCE étudiera votre besoin et vous recontactera pour préciser le périmètre.' });
      setQuote({ name:'', email:'', phone:'', company:'', service:quoteMode==='training'?['formations']:[], trainingChoices:[], trainingType:'', trainingParticipants:'', trainingLocation:'', deadline:'', details:'', consent:false });
      setQuoteStep(1);
    } catch (error) {
      toast({ title:'Envoi impossible', description:'La demande de devis n’a pas pu être transmise. Réessayez ou contactez-nous sur WhatsApp.', variant:'destructive' });
    } finally {
      setQuoteSending(false);
    }
  };

  return (
      <section id="formulaire-devis" className="quote-builder-section">
        <div className="quote-builder-shell">
          <aside className="quote-builder-intro">
            <h2>{quoteMode==='training'?'Préparons une formation adaptée à votre besoin.':'Construisons une proposition adaptée à votre besoin.'}</h2>
            <p>{quoteMode==='training'?'Choisissez vos thèmes, puis précisez le format et le nombre de participants avant de transmettre vos coordonnées.':'Choisissez votre prestation, précisez votre contexte et recevez une étude personnalisée. Les éléments transmis servent à cadrer le périmètre avant validation définitive.'}</p>
            <div>
              <article><ClipboardCheck/><b>Besoin clarifié</b><small>Votre demande est analysée avant toute proposition.</small></article>
              <article><Layers3/><b>Offre personnalisée</b><small>Le devis dépend des fonctionnalités et du niveau d’accompagnement.</small></article>
              <article><ShieldCheck/><b>Données protégées</b><small>Vos informations restent utilisées uniquement pour traiter la demande.</small></article>
            </div>
          </aside>
          <div className="quote-builder-form">
            <div className="quote-stepper"><span className={quoteStep===1?'active':'done'}><b>01</b>{quoteMode==='training'?'Choix des formations':'Choix des services'}</span><i/><span className={quoteStep===2?'active':''}><b>02</b>Vos coordonnées</span></div>
            {quoteSubmitted && <div className="quote-success"><CheckCircle2/>Votre demande a bien été enregistrée.</div>}
            <form onSubmit={submitQuote}>
              <input type="checkbox" name="botcheck" tabIndex="-1" autoComplete="off" className="hidden" />
              <div className={`quote-slide ${quoteStep===1?'active':'inactive-left'}`} aria-hidden={quoteStep!==1}>
                <div className="quote-selection-toolbar"><span>Configurez librement votre demande.</span><button type="button" disabled={quoteMode==='training'?!quote.trainingChoices.length:!quote.service.length} onClick={()=>setQuote((current)=>({...current,service:quoteMode==='training'?['formations']:[],trainingChoices:[]}))}>Tout désélectionner</button></div>
                {quoteMode==='digital' ? <><QuoteCategory title="Sites web & Landing Pages" note="Plusieurs choix possibles" items={QUOTE_GROUPS.web} selected={quote.service} onToggle={toggleService}/><QuoteCategory title="SaaS & logiciels métiers" note="Plusieurs choix possibles (tarification sur demande)" items={QUOTE_GROUPS.saas} selected={quote.service} onToggle={toggleService}/><QuoteCategory title="Hébergement" note="Une seule formule" items={QUOTE_GROUPS.hosting} selected={quote.service} onToggle={(value)=>chooseSingle(value,hostingValues)} disabledValues={standardDisabled?['hebergement-standard']:[]} message={hostingMessage} highlighted/><QuoteCategory title="Maintenance & sécurité" note="Une seule formule (tarification sur demande)" items={QUOTE_GROUPS.maintenance} selected={quote.service} onToggle={(value)=>chooseSingle(value,maintenanceValues)}/><QuoteCategory title="Services complémentaires" note="Un ou plusieurs choix possibles" items={digitalComplementary} selected={quote.service} onToggle={toggleService}/></> : <QuoteChoiceRow className="quote-training-choices" title="Choisissez vos formations" note="Un ou plusieurs choix possibles" choices={TRAINING_CHOICES} value={quote.trainingChoices} multiple onChange={(trainingChoice)=>setQuote((current)=>({...current,trainingChoices:current.trainingChoices.includes(trainingChoice)?current.trainingChoices.filter((choice)=>choice!==trainingChoice):[...current.trainingChoices,trainingChoice]}))}/>} 
                <div className="quote-next-row"><p>{quoteMode==='training'?`${quote.trainingChoices.length} formation${quote.trainingChoices.length>1?'s':''} sélectionnée${quote.trainingChoices.length>1?'s':''}`:`${quote.service.length} service${quote.service.length>1?'s':''} sélectionné${quote.service.length>1?'s':''}`}</p><button type="button" onClick={goToQuoteDetails}>Passer à l’étape suivante <ArrowRight/></button></div>
              </div>
              <div className={`quote-slide ${quoteStep===2?'active':'inactive-right'}`} aria-hidden={quoteStep!==2}>
                <div className="quote-details-heading"><button type="button" onClick={()=>setQuoteStep(1)}><span aria-hidden="true">←</span> Modifier {quoteMode==='training'?'les formations':'les services'}</button><h3>{quoteMode==='training'?'Finalisez votre demande.':'À qui devons-nous adresser la proposition ?'}</h3><p>{quoteMode==='training'?'Précisez le format, le nombre de participants et vos coordonnées.':'Les coordonnées sont obligatoires dès lors qu’une formule est sélectionnée.'}</p></div>
                {quoteMode==='training' && <div className="quote-training-details"><div><label htmlFor="training-type">Type de formation *</label><select id="training-type" required value={quote.trainingType} onChange={(e)=>setQuote({...quote,trainingType:e.target.value,trainingLocation:e.target.value==='En ligne'?'':quote.trainingLocation})}><option value="">Choisir un type</option><option>En ligne</option><option>Présentiel</option></select></div><fieldset className="quote-training-location" disabled={quote.trainingType!=='Présentiel'}><legend>Pays de la formation *</legend><div className="quote-country-choices">{['Sénégal','Maroc'].map((country)=><label key={country} className={quote.trainingLocation===country?'selected':''}><input type="radio" name="trainingLocation" value={country} required={quote.trainingType==='Présentiel'} checked={quote.trainingLocation===country} onChange={(e)=>setQuote({...quote,trainingLocation:e.target.value})}/><span>{country}</span></label>)}</div></fieldset><div><label htmlFor="training-participants">Nombre de participants *</label><input id="training-participants" name="trainingParticipants" type="number" min="1" required value={quote.trainingParticipants} onChange={(e)=>setQuote({...quote,trainingParticipants:e.target.value})}/></div><div><label htmlFor="training-deadline">Délai souhaité</label><select id="training-deadline" value={quote.deadline} onChange={(e)=>setQuote({...quote,deadline:e.target.value})}><option value="">À définir</option><option>Dès que possible</option><option>1 à 2 mois</option><option>3 à 6 mois</option><option>Plus de 6 mois</option></select></div></div>}
                <div className="quote-contact-grid"><Field label="Nom complet *" name="name" value={quote.name} onChange={(e)=>setQuote({...quote,name:e.target.value})} required/><Field label="Email *" name="email" type="email" value={quote.email} onChange={(e)=>setQuote({...quote,email:e.target.value})} required/><Field label="Téléphone / WhatsApp *" name="phone" type="tel" value={quote.phone} onChange={(e)=>setQuote({...quote,phone:e.target.value.replace(/[^0-9+()\s-]/g,'')})} required/><Field label="Entreprise / organisation" name="company" value={quote.company} onChange={(e)=>setQuote({...quote,company:e.target.value})}/>{quoteMode==='digital'&&<div><label>Délai souhaité</label><select value={quote.deadline} onChange={(e)=>setQuote({...quote,deadline:e.target.value})}><option value="">À définir</option><option>Dès que possible</option><option>1 à 2 mois</option><option>3 à 6 mois</option><option>Plus de 6 mois</option></select></div>}</div>
                <div className="quote-details"><label>Détails de votre projet <small>(optionnel)</small></label><textarea rows="5" value={quote.details} onChange={(e)=>setQuote({...quote,details:e.target.value})} placeholder="Objectifs, utilisateurs, fonctionnalités, contraintes, éléments déjà disponibles…"/></div>
                <AttachmentField />
                <label className="quote-consent"><input type="checkbox" required checked={quote.consent} onChange={(e)=>setQuote({...quote,consent:e.target.checked})}/><span>J’accepte que MCE utilise ces informations pour étudier ma demande. J’ai pris connaissance de la politique de confidentialité et des conditions générales de vente. Le devis devient contractuel uniquement après validation écrite des parties.</span></label>
                <div className="quote-submit-row quote-submit-final"><p>En envoyant ce formulaire, vos informations sont transmises à MCE uniquement dans le cadre de votre demande de devis.</p><button type="submit" disabled={quoteSending}>{quoteSending ? 'Envoi en cours…' : 'Envoyer ma demande de devis'} {!quoteSending && <ArrowRight/>}</button></div>
              </div>
            </form>
          </div>
        </div>

      </section>
  );
}

function QuoteCategory({ id, title, note, items, selected, onToggle, disabledValues=[], message, highlighted=false }) {
  const [activeInfo, setActiveInfo] = useState(null);
  useEffect(() => {
    const syncActiveInfo = (event) => setActiveInfo(event.detail || null);
    const closeInfo = (event) => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'pointerdown' && event.target.closest('.quote-option-info, .quote-info-panel')) return;
      setActiveInfo(null);
    };
    window.addEventListener('mce:quote-info', syncActiveInfo);
    document.addEventListener('keydown', closeInfo);
    document.addEventListener('pointerdown', closeInfo);
    return () => {
      window.removeEventListener('mce:quote-info', syncActiveInfo);
      document.removeEventListener('keydown', closeInfo);
      document.removeEventListener('pointerdown', closeInfo);
    };
  }, []);

  const toggleInfo = (value) => {
    window.dispatchEvent(new CustomEvent('mce:quote-info', {
      detail: value,
    }));
  };

  return (
    <fieldset id={id} className={`quote-category ${id?'scroll-mt-[88px] ':''}${highlighted?'quote-category-highlighted':''}`}>
      <legend><span>{title}</span><small>{note}</small></legend>
      {message&&<p className="quote-rule-message">{message}</p>}
      <div>
        {items.map(([value,label,price,description])=>{
          const disabled=disabledValues.includes(value);
          const infoId=`quote-info-${value}`;
          return (
            <div className="quote-option" key={value}>
              <label className={`${selected.includes(value)?'selected':''} ${disabled?'disabled':''}`}>
                <input type="checkbox" disabled={disabled} checked={selected.includes(value)} onChange={()=>onToggle(value)}/>
                <span><CheckCircle2/></span>
                <span><b>{label}</b>{price&&<small>{price}</small>}</span>
              </label>
              {description&&<>
                <button
                  type="button"
                  className={`quote-option-info ${activeInfo===value?'active':''}`}
                  aria-label={`En savoir plus sur ${label}`}
                  aria-expanded={activeInfo===value}
                  aria-controls={infoId}
                  onPointerEnter={(event)=>{
                    if (event.pointerType === 'mouse') toggleInfo(value);
                  }}
                  onPointerLeave={(event)=>{
                    if (event.pointerType === 'mouse') setActiveInfo(null);
                  }}
                  onFocus={(event)=>{
                    if (event.currentTarget.matches(':focus-visible')) toggleInfo(value);
                  }}
                  onBlur={()=>setActiveInfo(null)}
                  onClick={()=>{
                    if (window.matchMedia('(hover: none)').matches && activeInfo === value) setActiveInfo(null);
                    else toggleInfo(value);
                  }}
                >
                  <span aria-hidden="true">i</span>
                </button>
                {activeInfo===value && <QuoteInfoBubble id={infoId} label={label} description={description} />}
              </>}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

function QuoteInfoBubble({ id, label, description }) {
  const bubble = useRef(null);
  const [position, setPosition] = useState(null);
  useLayoutEffect(() => {
    const update = () => {
      const anchor = document.querySelector(`[aria-controls="${id}"]`);
      if (!anchor || !bubble.current) return;
      const rect = anchor.getBoundingClientRect();
      const width = bubble.current.offsetWidth;
      const height = bubble.current.offsetHeight;
      const left = Math.max(16, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 16));
      const above = rect.top >= height + 28;
      const top = Math.max(16, Math.min(above ? rect.top - height - 12 : rect.bottom + 12, window.innerHeight - height - 16));
      setPosition({ left, top, '--arrow-left': `${Math.max(18, Math.min(rect.left + rect.width / 2 - left, width - 18))}px`, '--arrow-top': above ? '100%' : '-7px', visibility: rect.bottom < 0 || rect.top > window.innerHeight ? 'hidden' : 'visible' });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [id]);
  return createPortal(<div ref={bubble} id={id} role="tooltip" className="quote-info-panel" style={position || { visibility: 'hidden' }}>
    <b>{label}</b>
    <p>{description}</p>
  </div>, document.body);
}

function QuoteChoiceRow({ className='', title, note='Un seul choix possible', choices, value, onChange, multiple=false }) {
  return <fieldset className={`quote-category quote-subcategory ${className}`}><legend><span>{title}</span><small>{note}</small></legend><div>{choices.map((choice)=>{const selected=multiple?value.includes(choice):value===choice;return <label key={choice} className={selected?'selected':''}><input type={multiple?'checkbox':'radio'} name={title} checked={selected} onChange={()=>onChange(choice)}/><span><CheckCircle2/></span><span><b>{choice}</b></span></label>})}</div></fieldset>;
}

function Field({ label, name, type='text', value, onChange, required }) {
  return (
    <div>
      <label className="text-[12px] font-semibold text-mce-navy/80">{label}</label>
      <input type={type} inputMode={type==='tel'?'tel':undefined} name={name} required={required} value={value} onChange={onChange} className={`mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-[14px] focus:border-mce-teal focus:outline-none focus:ring-2 focus:ring-mce-teal/30 transition-colors ${type==='tel'?'bg-white':''}`} />
    </div>
  );
}
